'use client'

import { useAtomValue, useStore } from 'jotai'
import { usePathname, useRouter } from 'next/navigation'
import { useContext, useState } from 'react'

import { useStatusBarShortcuts } from '@/components/widget/status-bar'
import { countAccountsAgainstPlanCap } from '@/features/account-access/lib/roles'
import { useAddDashboardWidget } from '@/features/dashboard/hooks/use-add-dashboard-widget'
import { COMPONENT_REGISTRY, type ComponentKey } from '@/features/dashboard/lib/component-registry'
import { LinkingPageContext } from '@/features/dashboard/lib/linking-page-context'
import { getTradePageIdFromPath, getTradePagePath } from '@/features/dashboard/lib/trade-page-path'
import { openAssistant } from '@/features/order/chat/lib/open-assistant'
import { orderPromptPrefillKey } from '@/features/order/order-form/components/order-prompt-bar'
import { useCapabilities } from '@/hooks/use-capabilities'
import { BILLING_ROUTE } from '@/lib/billing/routes'
import type { SettingTab } from '@/lib/configs/setting-config'
import { ANY_ORDER_FORM_SOURCE, resolveOrderFormTarget } from '@/lib/resolve-order-form-target'
import { useUserAccounts } from '@/service/account.hook'
import { composerPrefillRequestAtom } from '@/state/chat'
import { activePageIdAtom, dashboardLayoutsAtom } from '@/state/dashboard-layouts'
import { alignPageTabsToAccount } from '@/state/linking-group'
import {
  DEFAULT_ORDER_FORM_CARD_ID,
  forceOrderFormPopupCardIdAtom,
  registeredOrderContainersAtom,
} from '@/state/order'
import { settingsDialogRequestAtom } from '@/state/settings-dialog'
import { accountCardsSelectedAccountIdAtomFamily } from '@/state/widget-instances'
import { AccountStatus } from '@/types/generated/common/account'
import type { TradeVenue } from '@/types/generated/common/types'

// Module-level: COMPONENT_REGISTRY never changes, so build the list once.
export const DASHBOARD_WIDGET_COMPONENTS = Object.values(COMPONENT_REGISTRY)

/**
 * The dashboard-level actions shared by the right-click menu and the Cmd+K palette:
 * sheet/balance/page shortcuts, add a widget, align every widget to an account, and add
 * an account. Each consumer renders its own AccountConnectionSheet from
 * `showAddAccount` / `setShowAddAccount`.
 */
export function useDashboardCommands() {
  const router = useRouter()
  const store = useStore()
  const shortcuts = useStatusBarShortcuts()
  const { addWidget } = useAddDashboardWidget()

  // Same page-id scoping useLinkingGroupState resolves against — not layoutKey below,
  // which is dashboardLayoutsAtom's own (path-based) key for the widget grid.
  const linkingPageId = useContext(LinkingPageContext)
  const pathname = usePathname()
  const activePageId = useAtomValue(activePageIdAtom)
  const layoutKey = activePageId ? `/trade/${activePageId}` : pathname.split('?')[0]
  // The palette is mounted app-wide, outside any DashboardLayout, where the context reads its
  // 'default' fallback — the page to act on is then the active trade page.
  const targetPageId = linkingPageId !== 'default' ? linkingPageId : (activePageId ?? linkingPageId)
  const isOnDashboard = getTradePageIdFromPath(pathname.split('?')[0]) !== null
  // The order sheet and the widget grid only exist on a /trade/[pageId] dashboard; from any
  // other route, bring the user there so the result is visible. Atoms set before the push
  // (sheet open, composer prefill) are picked up once the dashboard mounts.
  const showDashboard = () => {
    if (isOnDashboard) return
    router.push(activePageId ? getTradePagePath(activePageId) : '/trade')
  }

  const { data: userAccountsData } = useUserAccounts()
  const { maxAccounts } = useCapabilities()
  const [showAddAccount, setShowAddAccount] = useState(false)
  const accounts = userAccountsData?.accounts ?? []
  const atAccountCap = countAccountsAgainstPlanCap(accounts) >= maxAccounts
  const activeAccounts = accounts.filter(
    (account) => account.status === AccountStatus.ACCOUNT_STATUS_ACTIVE
  )

  const addAccount = () => {
    if (atAccountCap) {
      router.push(BILLING_ROUTE)
      return
    }
    setShowAddAccount(true)
  }

  // Account Cards widgets keep their own selected account outside LinkingGroupState
  // (accountCardsSelectedAccountIdAtomFamily, keyed by widget id) — alignPageTabsToAccount
  // doesn't (and shouldn't) know about per-widget-type atoms, so point every
  // account-cards instance on this page at the chosen account here instead.
  const alignToAccount = (tradeVenue: TradeVenue, accountId: string) => {
    alignPageTabsToAccount(targetPageId, tradeVenue, accountId)
    const layouts = store.get(dashboardLayoutsAtom)
    const accountCardsWidgetIds = (layouts[layoutKey]?.activeComponents ?? [])
      .filter((widget) => widget.componentKey === 'account-cards')
      .map((widget) => widget.id)
    for (const widgetId of accountCardsWidgetIds) {
      store.set(accountCardsSelectedAccountIdAtomFamily(widgetId), [accountId])
    }
  }

  const goTo = (path: string) => router.push(path)

  // Opens the Settings dialog (GlobalSettingsDialog) over the current page instead of
  // routing to /settings; its maximize button still leads to the full page.
  const openSettings = (tab: SettingTab) => store.set(settingsDialogRequestAtom, { tab })

  // Prompt trading: trades are drafted by the Order Form's prompt bar (the assistant is
  // research-only). The text goes into the prompt bar of the Order Form widget on the page
  // (the last-focused one, else the first registered); only without one is the sheet opened
  // to host it. Put in, not sent — the user reviews and presses Enter.
  const startTradePrompt = (text: string) => {
    const target = resolveOrderFormTarget(store, ANY_ORDER_FORM_SOURCE)
    if (!store.get(registeredOrderContainersAtom).has(target)) {
      store.set(forceOrderFormPopupCardIdAtom, DEFAULT_ORDER_FORM_CARD_ID)
    }
    store.set(composerPrefillRequestAtom, { storageKey: orderPromptPrefillKey(target), text })
    showDashboard()
  }

  const openPopup = () => {
    shortcuts.openPopup()
    showDashboard()
  }

  const addWidgetAndShow = (componentKey: ComponentKey) => {
    addWidget(componentKey)
    showDashboard()
  }

  const openAssistantChat = () => openAssistant(store)

  // Research prompt: a fresh assistant thread that sends the text as its first message.
  const startResearchPrompt = (text: string) =>
    openAssistant(store, { thread: { kind: 'new' }, prompt: text })

  return {
    ...shortcuts,
    openPopup,
    layoutKey,
    goTo,
    openSettings,
    startTradePrompt,
    openAssistantChat,
    startResearchPrompt,
    addWidget: addWidgetAndShow,
    activeAccounts,
    alignToAccount,
    atAccountCap,
    addAccount,
    showAddAccount,
    setShowAddAccount,
  }
}
